/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable arrow-body-style */
/* eslint-disable no-nested-ternary */
import { Chip, FormControl, FormLabel, TextField } from '@material-ui/core';
import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete';
import { fetchUsers } from '@reducers/users';
import { usersSelector } from '@selectors/users';
import { User } from '@types';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, IntlShape, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-useless-path-segments
import DotStatus from './../DepartmentSelect/DotStatus';

export type UserValue = User | { id?: number };

export type UserChangeEventHandler<Multiple extends boolean = false> = (
  event: React.ChangeEvent<{}> | null,
  value: Multiple extends true ? User[] : User | null,
  reason: AutocompleteChangeReason
) => void;

export type UserSelectProps<Multiple extends boolean = boolean> = {
  label?: boolean | string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: Multiple;
  value: Multiple extends true ? UserValue[] : UserValue | null;
  onChange: UserChangeEventHandler<Multiple>;
  filterOptions?: (option: User) => boolean;
  disableInactiveOptions?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  role?: User['role'];
  useOnlineStatus?: boolean;
  error?: boolean;
};

declare function UserSelectComponent<Multiple extends boolean = boolean>(
  props: UserSelectProps<Multiple>
): JSX.Element;

const UserSelect: React.FC<UserSelectProps & { intl: IntlShape }> = ({
  label,
  placeholder,
  disabled,
  multiple,
  value,
  onChange,
  clearable,
  fullWidth,
  filterOptions,
  disableInactiveOptions,
  role,
  useOnlineStatus,
  error,
  intl: { formatMessage },
}) => {
  const { items, loading } = useSelector(usersSelector);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) dispatch(fetchUsers());
  }, [open]);

  const options: User[] = filterOptions ? items.filter(filterOptions) : items;

  useEffect(() => {
    if (
      !Array.isArray(value) &&
      value !== null &&
      value?.id === undefined &&
      options.length > 0
    ) {
      onChange(null, options[0], 'select-option');
    }
  }, [value, options, onChange]);

  const renderInput = useCallback(
    ({ InputProps, ...params }: AutocompleteRenderInputParams) => {
      const option = Array.isArray(value)
        ? undefined
        : options.find(({ id }) => id === value?.id);

      return (
        <TextField
          {...params}
          placeholder={
            placeholder ||
            formatMessage({ id: 'UserSelect.Placeholder' }, { role })
          }
          margin="dense"
          variant="outlined"
          InputProps={{
            ...InputProps,
            startAdornment: option ? (
              <span style={{ marginLeft: 8 }}>
                <DotStatus
                  status={
                    useOnlineStatus
                      ? option.online
                        ? 'ACTIVE'
                        : 'INACTIVE'
                      : option.status
                  }
                />
              </span>
            ) : (
              InputProps.startAdornment
            ),
          }}
        />
      );
    },
    [value, formatMessage, options, role, useOnlineStatus]
  );

  const getOptionLabel = useCallback(
    (option: User) => {
      return (
        option?.fullName ||
        options.find(({ id }) => id === option.id)?.fullName ||
        ' '
      );
    },
    [formatMessage, options]
  );

  const renderOption = useCallback((option: User) => {
    return (
      <>
        <DotStatus
          status={
            useOnlineStatus
              ? option.online
                ? 'ACTIVE'
                : 'INACTIVE'
              : option.status
          }
        />
        <span>{option.fullName}</span>
      </>
    );
  }, []);

  function getOptionDisabled(option: User) {
    return useOnlineStatus ? !option.online : !!option?.inactive;
  }

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      {label && (
        <FormLabel style={{ marginBottom: '0.5rem' }}>
          {label === true ? (
            <FormattedMessage id="UserSelect.Label" values={{ role }} />
          ) : (
            label
          )}
        </FormLabel>
      )}
      <Autocomplete
        className="department-select"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        multiple={multiple}
        autoComplete
        autoHighlight
        disableClearable={!clearable}
        disableCloseOnSelect={multiple}
        size="small"
        options={options}
        renderTags={renderTags}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        getOptionDisabled={
          disableInactiveOptions ? getOptionDisabled : undefined
        }
        renderInput={renderInput}
        renderOption={renderOption}
        value={value as User | User[]}
        onChange={onChange}
        loading={loading === 'pending'}
        disabled={disabled}
      />
    </FormControl>
  );
};

function renderTags(value: User[], getTagProps: AutocompleteGetTagProps) {
  return value.map((option, index) => (
    <Chip
      key={option.id}
      size="small"
      variant="outlined"
      label={option.localUsername}
      {...getTagProps({ index })}
    />
  ));
}

function getOptionSelected<T extends User>(option: T, value?: T) {
  return option?.id === value?.id;
}

export default injectIntl(UserSelect) as unknown as typeof UserSelectComponent;
